/**
 * Paces the reads of `inbox_items` that realtime events trigger.
 *
 * The view is expensive (it walks every conversation) and every admin tab
 * subscribes to the same tables, so an unpaced "refetch on every change" turned
 * a burst of messages into hundreds of full-view reads (the 2026-09-25 outage,
 * T-060). Two rules:
 *
 * - At most one read per `delayMs` window: the first event starts the timer and
 *   later ones ride along, so a steady stream can't starve the read either.
 * - Nothing while the tab is hidden. A change seen then is remembered and read
 *   once, straight away, when the tab is visible again.
 */
export interface Refetcher {
  /** A realtime change: read within `delayMs`, or on return if hidden. */
  schedule(): void;
  /** Read now (mount, filter change, poll) — or on return if hidden. */
  request(): void;
  /**
   * Another tab read at `startedAt`. If that was after the last change this tab
   * saw, the pending read is redundant and is dropped.
   */
  satisfiedAt(startedAt: number): void;
  dispose(): void;
}

const isHidden = () => document.visibilityState === 'hidden';

/**
 * `run` is handed the time of the latest change it is reading for, so a reader
 * can tell whether someone else's read already covers it.
 */
export function createRefetcher(run: (changedAt: number) => void, delayMs: number): Refetcher {
  let timer: number | undefined;
  /** When the latest unread change arrived; null when nothing is pending. */
  let pendingAt: number | null = null;

  const clearTimer = () => {
    window.clearTimeout(timer);
    timer = undefined;
  };

  const fire = () => {
    clearTimer();
    if (pendingAt === null || isHidden()) return;
    const changedAt = pendingAt;
    pendingAt = null;
    run(changedAt);
  };

  const onVisibility = () => {
    if (!isHidden()) fire();
  };
  document.addEventListener('visibilitychange', onVisibility);

  return {
    schedule() {
      pendingAt = Date.now();
      if (!isHidden() && timer === undefined) timer = window.setTimeout(fire, delayMs);
    },
    request() {
      pendingAt = Date.now();
      fire();
    },
    satisfiedAt(startedAt) {
      if (pendingAt !== null && startedAt >= pendingAt) {
        pendingAt = null;
        clearTimer();
      }
    },
    dispose() {
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
